import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useMemo } from 'react'
import { getBookedSlotStripColorForRoster } from 'src/clinic/components/ClinicManageBookingsPage/DayViewCalendarComponent'
import { BOOKING_STATUSES } from 'src/consumer/constants'
import { hexToRGBA } from 'src/consumer/helpers/utilFunctions'
import BookingEventInfoRenderer from './BookingEventInfoRenderer'
import RosterItemRenderer from './RosterItemRenderer'
import { StyledRosterCalendarComponentContainer } from './styledComponents'
import { aumhumScheduleToFullCalendarScheduleTransformer, FULL_CALENDAR_VIEW_TYPES } from './utils'

dayjs.extend(utc)

export default function RosterCalendar({
  calendarRef,
  hideAllDaySlot = false,
  handleDatesSet,
  handleEventClick,
  handleClickBookedEvent,
  onSlotSelect,
  rosterList = [],
  bookedSlots = [],
  legendItems,
  slotMinTime = '00:00:00',
  slotMaxTime = '24:00:00',
  rosterItemSchedules,
  getSidebarLegendValue,
  getSidebarLegendLabel,
  subHeading,
  getSidebarLegendValueBySecondaryKey,
  getSidebarItemReferenceFromRosterItem,
  blockedText = 'Blocked',
  slotBlockedText = 'Slot Blocked',
  hideCalendarHeader = false,
}) {
  const rosterCalendarEvents = useMemo(() => {
    return rosterList.map((rosterItem, index) => {
      const foundLegendItem = legendItems?.find((legItem) => {
        return getSidebarLegendValue(legItem) === getSidebarItemReferenceFromRosterItem(rosterItem)
      })

      const backgroundColor = hexToRGBA(foundLegendItem?.bgColor, 0.3)
      // const mentorItem  = getSidebarLegendLabel()
      return {
        id: rosterItem?.id, // Unique ID for each event
        title: !foundLegendItem ? blockedText : getSidebarLegendLabel(foundLegendItem), // You can customize this if needed
        start: `${rosterItem.customDate}T${rosterItem.slotStartTime}`, // Combine date and start time
        end: `${rosterItem.customDate}T${rosterItem.slotEndTime}`, // Combine date and end time
        // backgroundColor: foundLegendItem?.bgColor, // Use custom event color
        backgroundColor: backgroundColor,
        borderColor: '#666666', // Greyish border color
        borderStyle: 'dashed', // Make the border dashed
        borderWidth: '3px', // Border width
        eventClassNames: 'roster-event',
        textColor: foundLegendItem ? '#000' : '#fff',
        type: 'roster',
        legendItem: foundLegendItem,
        // backgroundColor: item.eventColor, // Use custom event color
        // borderColor: item.eventColor, // Match border color
        editable: foundLegendItem,
        durationEditable: false,
      }
    })
  }, [
    rosterList,
    getSidebarItemReferenceFromRosterItem,
    getSidebarLegendLabel,
    getSidebarLegendValue,
    legendItems,
    blockedText,
  ])
  const bookedEventCalendarEvents = useMemo(() => {
    return bookedSlots.map((bookedEventInfo, index) => {
      const foundLegendItem = legendItems?.find((legItem) => {
        return getSidebarLegendValueBySecondaryKey(legItem) === bookedEventInfo?.clinicId
      })

      const hideSlot = bookedEventInfo?.hideSlot

      // Convert UTC times to local timezone
      const localStartTime = dayjs.utc(bookedEventInfo.startTime).local().format('YYYY-MM-DDTHH:mm')
      const localEndTime = dayjs.utc(bookedEventInfo.endTime).local().format('YYYY-MM-DDTHH:mm')

      return {
        id: bookedEventInfo?.id, // Unique ID for each event
        title: hideSlot
          ? slotBlockedText
          : `(${bookedEventInfo?.patientInfo?.patientNumber}) ${bookedEventInfo?.patientInfo?.name}`, // You can customize this if needed
        start: localStartTime, // Using local timezone version
        end: localEndTime, // Using local timezone version
        type: 'booked-event-info',
        backgroundColor: getBookedSlotStripColorForRoster(
          bookedEventInfo,
          bookedEventInfo?.status === BOOKING_STATUSES.BOOKING_INITIATED,
        ).background, // Use custom event color
        // borderColor: foundLegendItem?.bgColor, // Use custom event color
        borderColor: getBookedSlotStripColorForRoster(
          bookedEventInfo,
          bookedEventInfo?.status === BOOKING_STATUSES.BOOKING_INITIATED,
        ).borderColor, // Use custom event color
        textColor: getBookedSlotStripColorForRoster(
          bookedEventInfo,
          bookedEventInfo?.status === BOOKING_STATUSES.BOOKING_INITIATED,
        ).fontColor,
        borderWidth: '2px',
        bookedEventInfo: { ...bookedEventInfo },
        legendItem: foundLegendItem,
        editable: true,
        durationEditable: false,
        // backgroundColor: item.eventColor, // Use custom event color
        // borderColor: item.eventColor, // Match border color
      }
    })
  }, [bookedSlots, legendItems, getSidebarLegendValueBySecondaryKey])
  const clinicWorkHours = aumhumScheduleToFullCalendarScheduleTransformer(rosterItemSchedules) || []

  function setTodayDate(info) {
    handleDatesSet({ from: info.start, to: info.end, viewType: info?.view?.type })
  }

  const handleSelect = (info) => {
    const start = info.startStr // Start time in ISO format
    const end = dayjs(info.endStr).subtract(1, 'second').toISOString() // End time in ISO format

    onSlotSelect(start, end, info.jsEvent) // Call parent method
  }

  function eventClickHandler(info) {
    const fcEvent = info.event
    const jsEvent = info.jsEvent

    const type = fcEvent?.extendedProps?.type
    if (type === 'roster') {
      const foundRosterItem = rosterList?.find((rosterItem) => rosterItem?.id === fcEvent?.id)
      handleEventClick(foundRosterItem, jsEvent)
    }
    if (type === 'booked-event-info') {
      const foundEvent = bookedSlots?.find((event) => event?.id === fcEvent?.id)
      handleClickBookedEvent({ bookedEventInfo: foundEvent })
    }
  }

  return (
    <StyledRosterCalendarComponentContainer>
      {!hideCalendarHeader && subHeading && (
        <div className="d-flex align-items-center justify-content-center">
          {subHeading && <h6>{subHeading}</h6>}
        </div>
      )}
      <FullCalendar
        ref={calendarRef}
        events={[
          ...(rosterCalendarEvents || []),
          ...clinicWorkHours,
          ...(bookedEventCalendarEvents || []),
        ]}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={
          !hideCalendarHeader
            ? {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }
            : null
        }
        initialView={FULL_CALENDAR_VIEW_TYPES.dayView}
        // editable={true}
        // slotDuration="00:15:00"
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        select={handleSelect}
        selectable={true}
        selectMirror={true}
        datesSet={setTodayDate}
        eventClick={eventClickHandler}
        allDaySlot={!hideAllDaySlot}
        eventContent={renderEventContent}
        // eventDurationEditable={false}
        // eventStartEditable={false}
        unselectAuto={false}
        dayMaxEvents={true}
        //   events={events} // alternatively, use the `events` setting to fetch from a feed
        //   select={handleDateSelect}
        //   eventDrop={handleDrop}
        //   eventClick={handleEventClick}
        //   eventsSet={handleEvents} // called after events are initialized/added/changed/removed
        /* you can update a remote database when these fire:
eventAdd={function(){}}
eventChange={function(){}}
eventRemove={function(){}}
*/
      />
    </StyledRosterCalendarComponentContainer>
  )
}

function renderEventContent(eventInfo) {
  const fcEvent = eventInfo.event
  const jsEvent = eventInfo.jsEvent

  const type = fcEvent?.extendedProps?.type

  const legendItem = fcEvent?.extendedProps?.legendItem
  const brandName = legendItem?.clinicBrandName
  const brandNameFirstLetter = legendItem?.clinicBrandName?.[0]

  if (type === 'roster') {
    return <RosterItemRenderer eventInfo={eventInfo} />
    // <div className="d-flex flex-wrap align-items-start">
    //   {brandNameFirstLetter && (
    //     <div
    //       style={{
    //         borderRadius: '90px',
    //         border: '1px solid black',
    //         aspectRatio: '1',
    //         width: '22px',
    //         textAlign: 'center',
    //       }}
    //     >
    //       {brandNameFirstLetter}
    //     </div>
    //   )}
    //   <PushPin sx={{ fontSize: '20px' }} />
    //   <b>{eventInfo.timeText}</b>
    //   <i>{eventInfo.event.title}</i>
    // </div>
  }
  if (type === 'booked-event-info') {
    return <BookingEventInfoRenderer eventInfo={eventInfo} />

    // handleEventClick(foundRosterItem, jsEvent)
  }
}
